import { AmbientGlow, Counter, GlassCard, ScrollReveal } from '@/components/design-system';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import {
  subscribeAllEventsContributions,
  subscribeManyEvents,
  type EventsContributionsResponse,
  type EventsResponse,
} from '@/lib/collections/events';
import { subscribeManyListings, type ListingsResponse } from '@/lib/collections/listings';
import { subscribeManyUsers, type UsersResponse } from '@/lib/collections/users';
import { Boxes, Building2, Recycle, Users } from 'lucide-react';

export function ImpactCounters() {
  const { data: users, loading: usersLoading } = useRealtimeData<UsersResponse[]>(
    subscribeManyUsers,
    true,
    ''
  );
  const { data: listings, loading: listingsLoading } = useRealtimeData<ListingsResponse[]>(
    subscribeManyListings,
    true,
    ''
  );
  const { data: events, loading: eventsLoading } = useRealtimeData<EventsResponse[]>(
    subscribeManyEvents,
    true,
    ''
  );
  const { data: contributions, loading: contributionsLoading } = useRealtimeData<
    EventsContributionsResponse[]
  >(subscribeAllEventsContributions, true, '', '');

  const loading = usersLoading || listingsLoading || eventsLoading || contributionsLoading;

  const households = (users ?? []).filter((u) => u?.role === 'household').length;
  const companies = (users ?? []).filter((u) => u?.role === 'company').length;
  const activeDrives = (events ?? []).filter((e) => e?.status === 'active').length;
  const listingsKg = (listings ?? []).reduce((sum, l) => sum + (l?.estimatedKg ?? 0), 0);
  const contributionsKg = (contributions ?? []).reduce((sum, c) => sum + (c?.kg ?? 0), 0);
  const totalKg = listingsKg + contributionsKg;

  const metrics = [
    { icon: Recycle, value: totalKg, suffix: 'kg', label: 'Plastic Recovered' },
    { icon: Users, value: households, suffix: '', label: 'Households Onboarded' },
    { icon: Building2, value: companies, suffix: '', label: 'Companies in Network' },
    { icon: Boxes, value: activeDrives, suffix: '', label: 'Active Community Drives' },
  ];

  return (
    <section className='relative overflow-hidden py-24 md:py-32'>
      <AmbientGlow color='primary' size={460} position='top-1/2 right-0 translate-x-1/2 -translate-y-1/2' />

      <div className='container relative'>
        <ScrollReveal className='mx-auto max-w-lg text-center'>
          <span className='text-overline'>Live From The Network</span>
          <h2 className='font-heading mt-2 text-headline text-foreground'>Real Impact, Right Now</h2>
        </ScrollReveal>

        <div className='mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          {metrics.map((m, i) => {
            const Icon = m.icon;
            return (
              <ScrollReveal key={m.label} delay={i * 0.08}>
                <GlassCard padding='lg' className='h-full'>
                  <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-accent/20'>
                    <Icon className='h-5 w-5 text-primary' strokeWidth={2} />
                  </div>
                  <div className='mt-5 font-heading text-3xl font-bold text-foreground md:text-4xl'>
                    <Counter value={m.value} suffix={m.suffix ? ` ${m.suffix}` : ''} />
                    {loading && !m.value ? (
                      <span className='ml-1 inline-block h-2 w-2 animate-pulse rounded-full bg-primary/50 align-middle' />
                    ) : null}
                  </div>
                  <p className='mt-1 text-sm text-muted-foreground'>{m.label}</p>
                </GlassCard>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default ImpactCounters;
